import React, { useState } from 'react';
import { trackEvent } from '../../utils/telemetry';

const LIVE_VIBES = [
  {
    id: 'tokyo_neon',
    name: 'Neon Tokyo Street Food',
    type: '30th Birthday',
    guests: 24,
    budget: '$1,200',
    palette: ['#09090B', '#F43F5E', '#A855F7', '#3B82F6', '#FACC15'],
    image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1200&q=80',
    glow: 'rgba(244, 63, 94, 0.25)',
    accent: '#F43F5E',
    buyTotal: 1840,
    rentTotal: 720,
    decorItems: ['Custom LED Kanji Neon Sign', 'Smoked Acrylic Cocktail Risers', 'Japanese Lantern Canopy'],
    timeline: ['5:00 PM • Ambient Synthwave & Welcome Mocktails', '6:30 PM • Live Yakitori & Gyoza Feast', '8:00 PM • Neon Cake Cut & DJ Set']
  },
  {
    id: 'tuscan_wine',
    name: 'Tuscan Sunset Vineyard Feast',
    type: 'Dinner Party',
    guests: 18,
    budget: '$900',
    palette: ['#451A03', '#B45309', '#D97706', '#F59E0B', '#FEF3C7'],
    image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1200&q=80',
    glow: 'rgba(217, 119, 6, 0.25)',
    accent: '#D97706',
    buyTotal: 1420,
    rentTotal: 580,
    decorItems: ['Olive Wood Platters & Urns', 'Terracotta Chargers & Linen', 'Festoon String Lighting'],
    timeline: ['6:00 PM • Chianti Spritz & Grazing Boards', '7:30 PM • Family-Style Porchetta & Pasta', '9:00 PM • Espresso Tiramisu Table']
  },
  {
    id: 'fairytale_garden',
    name: 'Pastel Fairytale Garden',
    type: 'Baby Shower',
    guests: 25,
    budget: '$750',
    palette: ['#701A75', '#EC4899', '#F472B6', '#FBCFE8', '#FDF4FF'],
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1200&q=80',
    glow: 'rgba(236, 72, 153, 0.25)',
    accent: '#EC4899',
    buyTotal: 1160,
    rentTotal: 490,
    decorItems: ['Circular Floral Moon Arch', 'Pressed Floral Tiered Cake Stand', 'Gauze Linen Runners'],
    timeline: ['11:00 AM • Welcome Rose Lemonade & Polaroid Wall', '12:00 PM • Brunch Buffet & Mimosa Station', '1:30 PM • Cake Reveal & Baby Predictions']
  }
];

function InteractiveStudioSandbox({ onStartPlanning }) {
  const [activeVibeId, setActiveVibeId] = useState('tokyo_neon');
  const [activeTab, setActiveTab] = useState('moodboard'); // 'moodboard' | 'variance' | 'timeline'
  const [guestCountMultiplier, setGuestCountMultiplier] = useState(1);
  const [timelineChecked, setTimelineChecked] = useState([false, false, false]);

  const currentVibe = LIVE_VIBES.find((v) => v.id === activeVibeId) || LIVE_VIBES[0];
  const adjustedGuests = Math.round(currentVibe.guests * guestCountMultiplier);
  const adjustedBuy = Math.round(currentVibe.buyTotal * guestCountMultiplier);
  const adjustedRent = Math.round(currentVibe.rentTotal * guestCountMultiplier);
  const savings = adjustedBuy - adjustedRent;

  const toggleTask = (index) => {
    setTimelineChecked((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  return (
    <div className="relative w-full max-w-full min-w-0 sm:max-w-lg lg:max-w-xl mx-auto overflow-hidden">
      {/* Dynamic Glowing Mesh Aura */}
      <div
        className="absolute inset-0 rounded-3xl blur-xl sm:blur-2xl opacity-30 transition-all duration-700 pointer-events-none overflow-hidden"
        style={{ background: `radial-gradient(circle at center, ${currentVibe.glow}, transparent 70%)` }}
      />

      {/* Main Glassmorphic Studio Sandbox */}
      <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-stone-200 shadow-xl sm:shadow-2xl overflow-hidden transition-all duration-300">
        {/* Sandbox Window Header */}
        <div className="px-3.5 sm:px-5 py-3 border-b border-stone-100 bg-stone-50/80 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-red-400" />
            <span className="w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-amber-400" />
            <span className="w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-emerald-400" />
            <span className="ml-1 sm:ml-2 text-xs font-bold text-stone-700 truncate">Studio Live Simulator</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold text-emerald-800 bg-emerald-50 px-2 sm:px-2.5 py-0.5 rounded-full border border-emerald-200/80 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span>Live Preview</span>
          </div>
        </div>

        {/* Vibe Selector Pills */}
        <div className="p-4 border-b border-stone-100 bg-stone-50/40">
          <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-2">
            Tap a party vibe to live-morph the studio:
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar sm:grid sm:grid-cols-3">
            {LIVE_VIBES.map((vibe) => {
              const isSelected = vibe.id === activeVibeId;
              return (
                <button
                  key={vibe.id}
                  onClick={() => {
                    setActiveVibeId(vibe.id);
                    setTimelineChecked([false, false, false]);
                  }}
                  className={`px-3 py-2 rounded-xl text-left transition-all text-xs font-bold flex flex-col justify-between border shrink-0 min-w-[110px] sm:min-w-0 ${
                    isSelected
                      ? 'bg-stone-900 text-white border-stone-900 shadow-sm scale-[1.02]'
                      : 'bg-white hover:bg-stone-100 text-stone-700 border-stone-200 shadow-2xs'
                  }`}
                >
                  <span className="whitespace-nowrap sm:whitespace-normal sm:line-clamp-1">{vibe.name}</span>
                  <span className={`text-[10px] font-normal ${isSelected ? 'text-stone-300' : 'text-stone-500'}`}>
                    {vibe.type}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Feature Sub-Tabs (Moodboard / Variance / Timeline) */}
        <div className="flex items-center justify-between border-b border-stone-100 bg-white px-2 sm:px-3 py-2 text-xs font-bold">
          <button
            onClick={() => setActiveTab('moodboard')}
            className={`px-2.5 sm:px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'moodboard'
                ? 'bg-stone-100 text-stone-900 shadow-2xs font-extrabold'
                : 'text-stone-500 hover:text-stone-800'
            }`}
          >
            <span>🎨</span>
            <span className="sm:hidden text-[11px]">Moodboard</span>
            <span className="hidden sm:inline">Room Moodboard</span>
          </button>
          <button
            onClick={() => setActiveTab('variance')}
            className={`px-2.5 sm:px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'variance'
                ? 'bg-stone-100 text-stone-900 shadow-2xs font-extrabold'
                : 'text-stone-500 hover:text-stone-800'
            }`}
          >
            <span>📊</span>
            <span className="sm:hidden text-[11px]">Savings</span>
            <span className="hidden sm:inline">Variance Savings</span>
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`px-2.5 sm:px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'timeline'
                ? 'bg-stone-100 text-stone-900 shadow-2xs font-extrabold'
                : 'text-stone-500 hover:text-stone-800'
            }`}
          >
            <span>⏱️</span>
            <span className="sm:hidden text-[11px]">Timeline</span>
            <span className="hidden sm:inline">Interactive Run-of-Show</span>
          </button>
        </div>

        {/* Interactive Tab Body */}
        <div className="p-5">
          {activeTab === 'moodboard' && (
            <div className="space-y-4 animate-in fade-in duration-300">
              <div className="relative aspect-[16/9] rounded-2xl overflow-hidden shadow-inner group">
                <img
                  src={currentVibe.image}
                  alt={currentVibe.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 text-white">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 bg-white/20 backdrop-blur-md rounded-full">
                      Spatial Focal Blueprint
                    </span>
                    <span className="text-xs font-semibold text-stone-200">
                      {adjustedGuests} Guests · Est. {currentVibe.budget}
                    </span>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold font-serif leading-tight">{currentVibe.name}</h4>
                </div>
              </div>

              {/* Live Color Swatches */}
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-1.5">
                  {currentVibe.palette.map((hex, i) => (
                    <div
                      key={i}
                      className="w-5 h-5 rounded-full border border-black/10 shadow-xs transition-transform hover:scale-110"
                      style={{ backgroundColor: hex }}
                      title={hex}
                    />
                  ))}
                  <span className="text-[11px] font-bold text-stone-500 ml-1">Palette</span>
                </div>
                <span className="text-[11px] font-semibold text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full">
                  3 Curated Decor Hotspots
                </span>
              </div>
            </div>
          )}

          {activeTab === 'variance' && (
            <div className="space-y-4 animate-in fade-in duration-300">
              {/* Interactive Guest Count Slider */}
              <div className="p-3.5 bg-stone-50 rounded-2xl border border-stone-200/80">
                <div className="flex items-center justify-between text-xs font-bold mb-1.5">
                  <span className="text-stone-700">Guest Count Slider:</span>
                  <span className="text-stone-900 bg-white px-2 py-0.5 rounded-lg border border-stone-200 shadow-2xs">
                    {adjustedGuests} Guests
                  </span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="2.5"
                  step="0.25"
                  value={guestCountMultiplier}
                  onChange={(e) => setGuestCountMultiplier(parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-stone-900"
                />
              </div>

              {/* Dynamic Variance Comparison */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-rose-50/60 border border-rose-200/80 rounded-2xl">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Retail Buy All</span>
                  <div className="text-lg font-extrabold text-rose-950 mt-0.5">${adjustedBuy}</div>
                  <p className="text-[10px] text-rose-800/80 mt-1">Unrented retail purchase cost</p>
                </div>
                <div className="p-3 bg-emerald-50 border border-emerald-200/80 rounded-2xl relative overflow-hidden">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">Rent-Optimized</span>
                  <div className="text-lg font-extrabold text-emerald-950 mt-0.5">${adjustedRent}</div>
                  <p className="text-[10px] font-bold text-emerald-700 mt-1">
                    ✨ Saves ${savings} (58% variance)
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-2.5 animate-in fade-in duration-300">
              <p className="text-[11px] font-semibold text-stone-500 mb-2">
                Click tasks to simulate real-time coordination:
              </p>
              {currentVibe.timeline.map((step, idx) => {
                const isChecked = timelineChecked[idx];
                return (
                  <div
                    key={idx}
                    onClick={() => toggleTask(idx)}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer ${
                      isChecked
                        ? 'bg-stone-100/70 border-stone-200 text-stone-400 line-through'
                        : 'bg-white border-stone-200/90 text-stone-800 hover:bg-stone-50 shadow-2xs'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {}}
                      className="w-4 h-4 text-stone-900 rounded border-stone-300 pointer-events-none"
                    />
                    <span className="text-xs font-semibold select-none">{step}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Action */}
        <div className="p-4 bg-stone-50/80 border-t border-stone-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="flex items-center justify-between sm:block">
            <span className="text-[10px] text-stone-500 uppercase font-bold tracking-wider block">Ready to plan?</span>
            <div className="text-xs font-bold text-stone-900">{currentVibe.name}</div>
          </div>
          <button
            onClick={() => {
              trackEvent('landing_cta_clicked', { cta: 'sandbox_launch', vibe: currentVibe.name });
              onStartPlanning();
            }}
            className="w-full sm:w-auto px-4 sm:px-5 py-2.5 bg-stone-900 hover:bg-stone-800 active:scale-95 text-white text-xs font-bold rounded-xl transition flex items-center justify-center gap-1.5 shadow-sm text-center"
          >
            <span>Launch Studio With This Vibe</span>
            <span>→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HeroSection({ onStartPlanning, onScrollToHowItWorks, onTryDemo, isAuthenticated, user }) {
  return (
    <section
      className="relative pt-24 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6 overflow-hidden w-full max-w-full"
      style={{ backgroundColor: 'var(--brand-cream)' }}
    >
      {/* Atmospheric Background Ambient Orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden max-w-full" style={{ contain: 'paint' }}>
        <div
          className="absolute -top-20 right-0 w-72 sm:w-96 h-72 sm:h-96 rounded-full opacity-30 translate-x-1/4 -translate-y-1/4"
          style={{ background: 'radial-gradient(circle, #FAF9F6, transparent 70%)' }}
        />
        <div
          className="absolute bottom-0 left-0 w-64 sm:w-80 h-64 sm:h-80 rounded-full opacity-25 -translate-x-1/4 translate-y-1/4"
          style={{ background: 'radial-gradient(circle, #FEF3C7, transparent 70%)' }}
        />
      </div>

      <div className="max-w-6xl w-full mx-auto relative z-10">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center w-full max-w-full">

          {/* Left Column: Editorial Value Proposition (6 cols) */}
          <div className="lg:col-span-6 w-full max-w-full min-w-0 text-center lg:text-left">
            {/* Eyebrow Badge */}
            <div className="inline-flex max-w-full items-center gap-2 px-3 sm:px-3.5 py-1.5 rounded-full text-[11px] sm:text-xs font-bold tracking-wider uppercase mb-5 sm:mb-6 bg-stone-100/90 text-stone-800 border border-stone-200/80 shadow-xs truncate">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
              <span className="truncate">Party Studio · AI Event Director</span>
            </div>

            {/* Headline */}
            <h1 className="font-extrabold leading-[1.12] mb-4 sm:mb-5 text-stone-950 tracking-tight text-2xl xs:text-3xl sm:text-4xl lg:text-6xl break-words">
              {isAuthenticated && user ? (
                <>Welcome back, <span className="font-serif italic font-normal text-stone-700">{user.name?.split(' ')[0] || 'Planner'}</span></>
              ) : (
                <>
                  Plan extraordinary parties.{' '}
                  <span className="block mt-1 sm:mt-2 font-serif italic font-normal text-stone-700">
                    Skip the endless spreadsheets.
                  </span>
                </>
              )}
            </h1>

            {/* Subhead */}
            <p className="text-sm sm:text-lg leading-relaxed mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0 text-stone-600 font-normal break-words">
              CraftMyParty turns raw ideas into bespoke themes, interactive 4-zone room moodboards, smart minute-by-minute timelines, and rent-optimized shopping blueprints.
            </p>

            {/* Primary Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center lg:justify-start gap-3 mb-4">
              <button
                onClick={() => {
                  trackEvent('landing_cta_clicked', { cta: 'start_planning', isAuthenticated: Boolean(isAuthenticated) });
                  onStartPlanning();
                }}
                className="group w-full sm:w-auto px-6 sm:px-7 py-3 sm:py-3.5 text-sm sm:text-base font-bold text-white rounded-full transition-all duration-200 hover:scale-[1.02] active:scale-95 bg-stone-950 hover:bg-stone-800 shadow-md flex items-center justify-center gap-2 text-center"
              >
                <span>{isAuthenticated ? 'Go to Studio' : 'Start Planning Free'}</span>
                <span className="inline-block group-hover:translate-x-1 transition-transform">&rarr;</span>
              </button>
              {onTryDemo && !isAuthenticated && (
                <button
                  onClick={() => {
                    trackEvent('landing_cta_clicked', { cta: 'explore_demo' });
                    onTryDemo();
                  }}
                  className="group w-full sm:w-auto px-5 sm:px-6 py-3 sm:py-3.5 text-sm sm:text-base font-semibold rounded-full border border-stone-300 transition-all duration-200 hover:bg-stone-100 hover:border-stone-400 flex items-center justify-center gap-2 bg-white/80 backdrop-blur-xs text-stone-800 active:scale-95 shadow-xs text-center"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="shrink-0">
                    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M6 5.5l5 2.5-5 2.5V5.5z" fill="currentColor" />
                  </svg>
                  <span>Explore Demo</span>
                </button>
              )}
            </div>
            <p className="text-[11px] sm:text-xs text-stone-500 mb-6 font-medium break-words text-center lg:text-left">
              ✨ No account or credit card required to start planning
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-4 sm:gap-x-6 gap-y-2 text-xs sm:text-sm font-medium text-stone-500">
              {['Free to start', 'No credit card required', '2-minute setup'].map((t) => (
                <span key={t} className="flex items-center gap-1.5 shrink-0">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="shrink-0">
                    <circle cx="7" cy="7" r="7" fill="#059669" opacity="0.15" />
                    <path d="M4 7l2 2 4-4" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{t}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Right Column: Live Interactive Party Studio Sandbox (6 cols) */}
          <div className="lg:col-span-6 w-full max-w-full min-w-0 flex justify-center lg:justify-end">
            <InteractiveStudioSandbox onStartPlanning={onStartPlanning} />
          </div>
        </div>
      </div>
    </section>
  );
}
